import '../operations.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { smsProvidersApi } from '../../api/smsProviders';
import { API_BASE_URL } from '../../api/client';
import type { SMSProviderRequest } from '../../api/smsProviders';
import type { SMSProvider } from '../../types';

const emptyForm: SMSProviderRequest = {
  name: '',
  provider_type: 'twilio',
  account_sid: '',
  auth_token: '',
  api_key: '',
  api_secret: '',
  from_number: '',
  extra_config: '',
  inbound_enabled: true,
  report_keyword: 'REPORT',
};

function toFormState(provider: SMSProvider | null): SMSProviderRequest {
  if (!provider) return { ...emptyForm };
  let extra: { inbound_enabled?: boolean; report_keyword?: string } = {};
  try {
    extra = provider.extra_config ? JSON.parse(provider.extra_config) : {};
  } catch {
    extra = {};
  }
  return {
    name: provider.name,
    provider_type: provider.provider_type,
    account_sid: provider.account_sid,
    auth_token: '',
    api_key: provider.api_key,
    api_secret: '',
    from_number: provider.from_number,
    extra_config: provider.extra_config,
    inbound_enabled: extra.inbound_enabled ?? false,
    report_keyword: extra.report_keyword ?? 'REPORT',
  };
}

export function SMSProviderList() {
  const queryClient = useQueryClient();
  const [formModal, setFormModal] = useState<{ open: boolean; provider: SMSProvider | null }>({
    open: false,
    provider: null,
  });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; provider: SMSProvider | null }>({
    open: false,
    provider: null,
  });
  const [form, setForm] = useState<SMSProviderRequest>(emptyForm);
  const [webhookSetup, setWebhookSetup] = useState<string | null>(null);

  const { data: providers, isLoading } = useQuery({
    queryKey: ['smsProviders'],
    queryFn: async () => {
      const res = await smsProvidersApi.getAll();
      return res.data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: (payload: SMSProviderRequest & { id?: number }) =>
      payload.id ? smsProvidersApi.update(payload.id, payload) : smsProvidersApi.create(payload),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ['smsProviders'] });
      setFormModal({ open: false, provider: null });
      setForm(emptyForm);
      const apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
      setWebhookSetup(data.webhook_path ? `${apiOrigin}${data.webhook_path}` : null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => smsProvidersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smsProviders'] });
      setDeleteModal({ open: false, provider: null });
    },
  });

  const openCreate = () => {
    setForm(emptyForm);
    setFormModal({ open: true, provider: null });
  };

  const openEdit = (provider: SMSProvider) => {
    setForm(toFormState(provider));
    setFormModal({ open: true, provider });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: SMSProviderRequest & { id?: number } = { ...form };
    if (form.provider_type === 'httpsms') {
      payload.extra_config = JSON.stringify({
        inbound_enabled: Boolean(form.inbound_enabled),
        report_keyword: form.report_keyword?.trim() ?? '',
      });
    }
    if (formModal.provider) payload.id = formModal.provider.id;
    saveMutation.mutate(payload);
  };

  const needsAuthToken = form.provider_type === 'twilio';
  const needsApiSecret = form.provider_type === 'vonage';
  const needsHTTPSMS = form.provider_type === 'httpsms';

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (p: SMSProvider) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg"><MessageSquare className="w-4 h-4 text-indigo-600" /></div>
          <p className="font-medium">{p.name}</p>
        </div>
      ),
    },
    { key: 'provider_type', header: 'Type', render: (p: SMSProvider) => <span className="text-sm text-gray-500 capitalize">{p.provider_type}</span> },
    { key: 'from_number', header: 'From', render: (p: SMSProvider) => <span className="text-sm text-gray-500">{p.from_number}</span> },
    {
      key: 'actions',
      header: '',
      render: (p: SMSProvider) => (
        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={() => openEdit(p)} className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit" aria-label="Edit">
            <Edit className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setDeleteModal({ open: true, provider: p })} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" aria-label="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="operations-eyebrow">Simulation delivery</p>
          <h1 className="text-2xl font-bold text-gray-900">SMS Providers</h1>
          <p className="text-gray-500 mt-1">Configure gateways for smishing campaigns</p>
        </div>
        <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" />New Provider</Button>
      </div>
      <Card>
        {isLoading ? (
          <div className="animate-pulse space-y-4">{[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded" />)}</div>
        ) : (
          <Table columns={columns} data={providers ?? []} keyExtractor={(p) => p.id} emptyMessage="No SMS providers configured." />
        )}
      </Card>

      <Modal isOpen={formModal.open} onClose={() => setFormModal({ open: false, provider: null })} title={formModal.provider ? 'Edit SMS Provider' : 'New SMS Provider'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" htmlFor="sms-provider-name">Name</label>
            <input id="sms-provider-name" type="text" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <label className="label" htmlFor="sms-provider-type">Provider type</label>
            <select id="sms-provider-type" className="input" value={form.provider_type} onChange={(e) => setForm({ ...form, provider_type: e.target.value as SMSProviderRequest['provider_type'] })}>
              <option value="twilio">Twilio</option>
              <option value="vonage">Vonage</option>
              <option value="generic">Generic webhook</option>
              <option value="httpsms">httpSMS</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="sms-provider-from">From number or sender ID</label>
            <input id="sms-provider-from" type="text" className="input" value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} required />
          </div>
          {needsAuthToken && (
            <>
              <div>
                <label className="label" htmlFor="sms-provider-account-sid">Account SID</label>
                <input id="sms-provider-account-sid" type="text" className="input" value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} />
              </div>
              <div>
                <label className="label" htmlFor="sms-provider-auth-token">Auth token</label>
                <input id="sms-provider-auth-token" type="password" className="input" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} placeholder={formModal.provider ? 'Leave blank to keep unchanged' : ''} />
              </div>
            </>
          )}
          {needsApiSecret && (
            <>
              <div>
                <label className="label" htmlFor="sms-provider-api-key">API key</label>
                <input id="sms-provider-api-key" type="text" className="input" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} />
              </div>
              <div>
                <label className="label" htmlFor="sms-provider-api-secret">API secret</label>
                <input id="sms-provider-api-secret" type="password" className="input" value={form.api_secret} onChange={(e) => setForm({ ...form, api_secret: e.target.value })} placeholder={formModal.provider ? 'Leave blank to keep unchanged' : ''} />
              </div>
            </>
          )}
          {needsHTTPSMS && (
            <>
              <div>
                <label className="label" htmlFor="httpsms-api-key">httpSMS API key</label>
                <input id="httpsms-api-key" type="password" className="input" autoComplete="new-password" value={form.api_key ?? ''} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder={formModal.provider ? 'Leave blank to keep unchanged' : 'Paste your key from httpsms.com/settings'} required={!formModal.provider} />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={Boolean(form.inbound_enabled)} onChange={(e) => setForm({ ...form, inbound_enabled: e.target.checked })} />
                <span>Enable inbound replies</span>
              </label>
              <div>
                <label className="label" htmlFor="httpsms-report-keyword">Report keyword</label>
                <input id="httpsms-report-keyword" type="text" className="input" value={form.report_keyword ?? ''} onChange={(e) => setForm({ ...form, report_keyword: e.target.value })} placeholder="REPORT" />
                <p className="text-sm text-gray-500 mt-1">A reply with this keyword is recorded as a reported SMS. STOP and UNSUBSCRIBE always opt the number out.</p>
              </div>
              <p className="text-sm text-gray-500">Install the httpSMS Android app, link it to your account, and select the sending SIM in the phone app. When inbound replies are enabled, AwareNow will show a callback URL after you save this provider.</p>
            </>
          )}
          {form.provider_type === 'generic' && (
            <>
              <div>
                <label className="label">Extra config (JSON)</label>
                <textarea className="input" rows={3} value={form.extra_config} onChange={(e) => setForm({ ...form, extra_config: e.target.value })} placeholder={`{"endpoint":"...","method":"POST","headers":{},"body_template":"..."}`} />
              </div>
              <p className="text-sm text-gray-500">For generic providers, provide endpoint, method, headers, and body_template in JSON.</p>
            </>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setFormModal({ open: false, provider: null })}>Cancel</Button>
            <Button type="submit" loading={saveMutation.isPending}>Save</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={Boolean(webhookSetup)} onClose={() => setWebhookSetup(null)} title="httpSMS inbound webhook" size="md">
        <p className="text-sm text-gray-600 mb-3">Copy this callback URL into your httpSMS webhook settings now. It contains a one-time secret and will not be shown again.</p>
        <label className="label" htmlFor="httpsms-webhook-url">Callback URL</label>
        <input id="httpsms-webhook-url" className="input" value={webhookSetup ?? ''} readOnly onFocus={(e) => e.currentTarget.select()} />
        <div className="flex justify-end mt-4"><Button onClick={() => setWebhookSetup(null)}>Done</Button></div>
      </Modal>

      <Modal isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, provider: null })} title="Delete SMS provider" size="sm">
        <p>Delete <strong>{deleteModal.provider?.name}</strong>? Campaigns using it will fail to send.</p>
        <div className="flex justify-end gap-3 mt-4">
          <Button type="button" variant="secondary" onClick={() => setDeleteModal({ open: false, provider: null })}>Cancel</Button>
          <Button type="button" variant="danger" onClick={() => deleteModal.provider && deleteMutation.mutate(deleteModal.provider.id)} loading={deleteMutation.isPending}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
}
